import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MaskPipe } from 'ngx-mask';
import { Subscription } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { TranslateKey } from '../../../common/pipes/translate-key.pipe';
import { CommonService } from '../../../shared/services/common.service';
import { IsoCodeThree } from '../../common/constants';
import { VatIdValidations } from '../../constants/vatid-validations.constants';
import { AddressSchemaFormFrameworkType } from '../../enums/address-type.enum';
import { SchemaProperty } from '../../enums/schema-property.enum';
import { BonusInfo } from '../../interface/bonus-info.interface';
import { JsonSchemaFormOptions } from '../../interface/enrollment/personal-info.interface';
import { PersonalInfoConfig } from '../../interface/personal-info-config.interface';
import { AddressService } from '../../services/address.service';
import { ConfigurationService } from '../../services/configuration.service';
import { PersonalInfoConfigService } from '../../services/personal-info-config.service';
import { PersonalMessageService } from '../../services/personal-schema-form.service';
import { CustomDatePickerComponent } from '../../widgets/custom-datepicker.widget';
import { CustomMaskTextboxComponent } from '../../widgets/custom-mask-textbox.widget';
import { CustomSelectWidgetComponent } from '../../widgets/custom-select.widget';
import { CustomInputComponent } from '../../widgets/custom-textbox.widget';
import { MessageService } from './../../../shared/services/message.service';


/**
 * @description this component help to
 * validate the bonus info
 * @date 2020-09-14
 * @export
 * @class BonusInfoFormComponent
 * @implements {OnInit}
 */
@Component({
    selector: 'app-bonus-info-form',
    templateUrl: '../../templates/template1/views/enrollment/bonus-schema-info.component.html',
    styleUrls: ['../../templates/template1/themes/default/css/personal-info-form.component.less']
})

export class BonusInfoFormComponent implements OnInit, OnDestroy {

    @Input() bonusInfoSchemaUrl: string;
    @Input() preloadBonusInformation: BonusInfo;
    @Output() bonusInfoSubmit = new EventEmitter<any>();
    @Output() bonusInfoSubmitOnInputChange = new EventEmitter<any>();
    personalInfoConfig: PersonalInfoConfig;

    jsonFormObject: any;
    enrollCountryCode: string;
    originalJsonFormObject: any;
    selectedFrameWork = AddressSchemaFormFrameworkType.BOOTSTRAP4;
    jsonFormOptions: JsonSchemaFormOptions = {
        addSubmit: true, // Add a submit button if layout does not have one
        loadExternalAssets: false, // Load external css and JavaScript for frameworks
        debug: false,
        setSchemaDefaults: true,
        returnEmptyFields: false,
    };
    liveFormData: BonusInfo;
    bonusInfoFormValidated: boolean;
    customWidgets = {
        'custom-select-widget': CustomSelectWidgetComponent,
        'custom-input-widget': CustomInputComponent,
        'custom-mask-widget': CustomMaskTextboxComponent,
        'custom-datepicker-widget': CustomDatePickerComponent
    };
    interval: NodeJS.Timer;
    bonusInfoFormSubscription: Subscription;
    validationMessageInitialHide = true;
    bonusInformation: BonusInfo;
    displayBonusInfoForm = false;
    customSelectSubscription:Subscription;
    disableBonusInfoFormSubscription:Subscription;
    activeEle: any;
    nldBonusInfoForm: any;
    nldBonusInfo: any;


    constructor(
        private _addressService: AddressService,
        private _elementRef: ElementRef,
        private _renderer: Renderer,
        private _chRef: ChangeDetectorRef,
        private _translatePipe: TranslateService,
        private _translateKey: TranslateKey,
        private _maskPipe: MaskPipe,
        private _commonService: CommonService,
        private _personalInfoConfigService: PersonalInfoConfigService,
        private _configurationService: ConfigurationService,
        private _personalMessageService: PersonalMessageService,
        private _messageService: MessageService
    ) { }

    ngOnInit() {
        this.loadDefaultSettings();
    }

    /**
    * @description it used to load default configuration
    * of the current component based on user
    * @date 2020-09-16
    * @private
    * @memberof BonusInfoFormComponent
    */
    private loadDefaultSettings(): void {
        this.enrollCountryCode = this._configurationService.getEnrollCountry();
        this.personalInfoConfig = this._personalInfoConfigService.getSettings(this.enrollCountryCode);
        this.getBonusInfoJsonSchema();
        this.bonusInfoFormSubject();
        this.rebindBonusInfoFormSubject();
        this.getCustomBankTypeSelectOption();
        this.disableBonusInfoForm();
    }

    /**
    * @description this method used
    * subscribe changes of form submit
    * event
    * @date 2018-07-24
    * @private
    * @memberof BonusInfoFormComponent
    */
    private bonusInfoFormSubject(): void {
        this.bonusInfoFormSubscription = this
            ._personalMessageService.getSubmitBonusInfoForm()
            .subscribe(isSubmit => {
                this.submitBonusInfoForm();
            });
    }

    /** this method will enable/disable the bonus info form */
    private disableBonusInfoForm(): void {
        this.disableBonusInfoFormSubscription = this
            ._personalMessageService.getDisableBonusInfoForm()
            .subscribe((isDisable) => {
                const addressElements: any = document.querySelectorAll('.disable_field input, .disable_field select');
                if (addressElements) {
                    for (let i = 0; i < addressElements.length; i++) {
                        addressElements[i].disabled = isDisable ? true : false;
                    }
                }
            });
    }


    /**
     * @description this method will subscribe changes
     * when we need to submit bonus info
     * @date 2020-09-16
     * @memberof BonusInfoFormComponent
     */
    submitBonusInfoForm(): void {
        // submiting bonus information
        if (!this.bonusInfoFormValidated) {
            const element = this._elementRef.nativeElement.getElementsByClassName('bonus-info__btn');
            if (element && element[0]) {
                element[0].click();
            }
        }
    }

    /**
     * to get the country specific schema json file
     * @returns void
     */
    getBonusInfoJsonSchema(): void {
        this._addressService.getJsonFormSchema(this.bonusInfoSchemaUrl)
            .subscribe((response: any) => {
                this.jsonFormObject = this.getParsedSchemaFormJson(response);
                this.originalJsonFormObject = response;
                this.prePopulateUserInfoFields(this.jsonFormObject);
             //   this.findErrorElement();
            });
    }

     /**
    * @description this method used
    * subscribe changes of form submit
    * event
    * @date 2018-07-24
    * @private
    * @memberof PersonalInfoComponent
    */
   private rebindBonusInfoFormSubject(): void {
    this.bonusInfoFormSubscription = this
        ._personalMessageService.getRebindBonusinfoForm()
        .subscribe(isSubmit => {
            this.enrollCountryCode = this._configurationService.getEnrollCountry();
            this.personalInfoConfig = this._personalInfoConfigService.getSettings(this.enrollCountryCode);
            this.bonusInfoSchemaUrl = this._configurationService.getBonusInfoSchemaUrl();
            this.getBonusInfoJsonSchema();
        });
        if (this.personalInfoConfig.countryCode.toLowerCase() === IsoCodeThree.NLD.toLocaleLowerCase()){
            this.nldBonusInfo = true;
        } else {
            this.nldBonusInfo = false;
        }

    }

/** this method will triggeres 
 * when bonus info Bank name changes 
*/

    getCustomBankTypeSelectOption(): void {
        this.customSelectSubscription = this._messageService.getBankTypeChange()
            .subscribe((bankCode: any) => {
                this.enrollCountryCode = this._configurationService.getEnrollCountry();
                if (this.enrollCountryCode.toLowerCase() === IsoCodeThree.SWE.toLocaleLowerCase() || this.enrollCountryCode.toLowerCase() === IsoCodeThree.AUT.toLocaleLowerCase() || this.enrollCountryCode.toLowerCase() === IsoCodeThree.DEU.toLocaleLowerCase()) {
                    if (bankCode) {
                        this.bonusInfoSchemaUrl = this.enrollCountryCode.toLowerCase() + '-' + bankCode.toLowerCase() + '-bonus-info.json?templateVersion= '
                            + environment.templateVersion;
                    } else {
                        this.bonusInfoSchemaUrl = this.enrollCountryCode.toLowerCase() + '-bankgiro-bonus-info.json?templateVersion= '
                            + environment.templateVersion;
                    }
                } else {
                    this.bonusInfoSchemaUrl = this.enrollCountryCode.toLowerCase() + '-bonus-info.json?templateVersion= '
                        + environment.templateVersion;
                }
                this._configurationService.setBonusInfoSchemaUrl(this.bonusInfoSchemaUrl);
                this._personalMessageService.setRebindBonusinfoForm(true);
            });
            
    }

    /**
     * get parsed json form object
     * @param  {any} jsonText
     * @returns any
     */
    getParsedSchemaFormJson(jsonText: any): any {
        let newFormObject: any = null;
        try {
            // Parse entered content as JSON
            newFormObject = JSON.parse(jsonText);
        } catch (jsonError) {
            try {
                // If entered content is not valid JSON,
                // parse as JavaScript instead to include functions
                newFormObject = null;

            } catch (javascriptError) {
                return;
            }
        }
        return newFormObject;
    }

    /**
     * @returns void
     */
    findErrorElement(): void {
        this.interval = setInterval(() => {
            if (document.getElementsByClassName('help-block').length > 0) {
                this.hideErrorElement(true);
            }
        }, 150000);
        setTimeout(() => {
            this.validationMessageInitialHide = false;
        }, 150000);
    }

    /**
     * @param  {boolean} hide
     * @returns void
     */
    private hideErrorElement(hide: boolean): void {
        const elements = this._elementRef.nativeElement.getElementsByClassName('help-block');
        if (elements) {
            for (let i = 0; i < elements.length; i++) {
                if (hide) {
                    this._renderer.setElementClass(elements[i], 'hide', true);
                    this._renderer.setElementClass(elements[i], 'show', false);
                } else {
                    this._renderer.setElementClass(elements[i], 'show', true);
                    this._renderer.setElementClass(elements[i], 'hide', false);
                }
            }
            clearInterval(this.interval);
        }
    }


    /**
     * @description this method will help to
     * load data while changing the inputvalue
     * @date 2020-09-15
     * @param {BonusInformation} data
     * @memberof BonusInfoFormComponent
     */
    onChanges(data: BonusInfo) {
        this.liveFormData = data;
        this.activeEle = document.activeElement;
        if (this.activeEle) {
           // document.getElementById(this.activeEle).focus();
            setTimeout(() => {
                const ele =document.getElementById(this.activeEle);
                if (ele && ele[0]) {
                    ele[0].focus();
                }
            }, 0);
        }
        if (this.personalInfoConfig.bonusInfoDynamicValidation) {
            this.reloadJsonObject();
        }
    }

    /**
     * @description it will trigger when we
     * change any input value in form
     * @date 2018-08-02
     * @memberof AddressComponent
     */
    changeInputValue(event: any): void {
        this.bonusInfoFormValidated = false;
        this._messageService.setBlxBonusInformation(event.accountNumber);
        if(event && event.bsnNumber){
            this._messageService.setBSNNumber(event.bsnNumber);
        }       
        this.bonusInfoSubmitOnInputChange.emit(this.bonusInfoFormValidated);
    }

    /**
     * @description new Address Schema Form
     * @date 2020-09-16
     * @private
     * @memberof AddressComponent
     */
    private reloadJsonObject(): void {
        if (this.liveFormData && (this.liveFormData.accountName
            || this.liveFormData.accountNumber || this.liveFormData.sortCode
            || this.liveFormData.accountReferenceNumber)) {
            const jsonObject = this.getParsedSchemaFormJson(this.originalJsonFormObject);
            const jsonSchemaKeys = Object.keys(jsonObject.schema.properties);
            jsonSchemaKeys.forEach((obj: any) => {
                if (obj === SchemaProperty.ACCOUNTNAME || (obj === SchemaProperty.ACCOUNTNUMBER && (jsonSchemaKeys.findIndex((ob)=> ob === SchemaProperty.BANKCODE) === -1))  || (obj === SchemaProperty.SORTCODE && (jsonSchemaKeys.findIndex((ob)=> ob === SchemaProperty.BANKCODE) === -1))) {
                    jsonObject.schema.required.push(obj);
                    jsonObject.schema.properties[obj].placeholder = jsonObject.schema.properties[obj].placeholder + '*';
                }
            });
            jsonObject.data = this.liveFormData;
            this.jsonFormObject = jsonObject;
        } else if (!this.liveFormData || (this.liveFormData &&
            !this.liveFormData.accountName && !this.liveFormData.accountNumber
             && !this.liveFormData.sortCode && !this.liveFormData.accountReferenceNumber)) {
                const jsonObject = this.getParsedSchemaFormJson(this.originalJsonFormObject);
            const jsonSchemaKeys = Object.keys(jsonObject.schema.properties);
            jsonSchemaKeys.forEach((obj: any) => {
                if (obj === SchemaProperty.ACCOUNTNAME || (obj === SchemaProperty.ACCOUNTNUMBER && (jsonSchemaKeys.findIndex((ob)=> ob === SchemaProperty.BANKCODE) === -1))  || (obj === SchemaProperty.SORTCODE && (jsonSchemaKeys.findIndex((ob)=> ob === SchemaProperty.BANKCODE) === -1))) {
                    jsonObject.schema.required.splice(jsonObject.schema.required.indexOf(obj), 1);
                    if (jsonObject.schema.properties[obj].placeholder.endsWith('*')) {
                        jsonObject.schema.properties[obj].placeholder = jsonObject.schema.properties[obj].placeholder.slice(0, -1);
                    }
                }
            });
            jsonObject.data = this.liveFormData;
            this.jsonFormObject = jsonObject;
        }
    }

    /**
     * @description this method will be used to perpopulate
     * user details in schma form
     * @date 2020-09-15
     * @param {*} jsonSchemaFormObject
     * @memberof BonusInfoFormComponent
     */
    prePopulateUserInfoFields(jsonSchemaFormObject: any): void {
        this.jsonFormObject = jsonSchemaFormObject;
        if (this.preloadBonusInformation) {
            if (this.preloadBonusInformation.vatId) {
                const vatInfo = VatIdValidations.formats.find(item =>
                    item.countryCode.toLowerCase() === this.enrollCountryCode.toLowerCase());
                this.preloadBonusInformation.vatId = this._maskPipe.transform(this.preloadBonusInformation.vatId, vatInfo.maskFormat);
            }
            this.jsonFormObject.data = this.preloadBonusInformation;
        } else {
            this.jsonFormObject.data = this.preloadBonusInformation;
        }
        if(this.nldBonusInfo && this.personalInfoConfig.countryCode.toLowerCase() === IsoCodeThree.NLD.toLocaleLowerCase()){
            let jsonSchema=this.jsonFormObject
            delete jsonSchema.schema.properties.bsnNumber;
            delete jsonSchema.form[0].items[0].items[1];
            this.jsonFormObject =jsonSchema;
        }
        this.getTranslatedSchemaData(this.jsonFormObject);
        setTimeout(() => {
            if (this.preloadBonusInformation && this.preloadBonusInformation.isAutoPopulate) {
                this._personalMessageService.setVatIdChanged(true);
            }
        }, 0);
    }
     /**
     * @description it used to find the custom errors
     * based on unique validations
     * @date 2020-09-15
     * @returns {any[]}
     * @memberof BonusInfoFormComponent
     */
    getCustomErrors(bonusInfo: BonusInfo): any[] {
        const customErrors = [];
        const length: number = sessionStorage.length;
        for (let i = 0; i < length; i++) {
            const key = sessionStorage.key(i);
            if (!(sessionStorage[key] === 'true') && key.indexOf('customError-') > -1) {
                customErrors.push(sessionStorage[key]);
            }
        }
        return customErrors;
    }

    /**
      * to save bonus info
      * @param  {any} address
      * @returns void
      */
     
     saveBonusInfo(bonusInfo: BonusInfo): void {
        const customErrors = this.getCustomErrors(bonusInfo);
        if (bonusInfo) {
            if ((!customErrors || (customErrors
                && !customErrors.find(x => x === 'InProgress')
                && (customErrors.length === 0 || customErrors[0] === '')))) {
                setTimeout(() => {
                    if (!customErrors || (customErrors
                        && !customErrors.find(x => x === 'InProgress')
                        && (customErrors.length === 0 || customErrors[0] === ''))) {
                        this.submitBonusInfo(bonusInfo, true);
                    } else if (this.preloadBonusInformation
                        && this.preloadBonusInformation.isAutoPopulate) {
                        this.submitBonusInfo(bonusInfo, true);
                    } else {
                        this.submitBonusInfo(bonusInfo, false);
                    }
                }, 2000);
            } else {
                this.submitBonusInfo(bonusInfo, false);
                this.hideErrorElement(false);
            }
        } else {
            this.submitBonusInfo(bonusInfo, false);
            this.hideErrorElement(false);
        }
    }

    /**
     * @description this method will emit the perosnal information
     * @date 2020-09-15
     * @param {BonusInformation} bonusInfo
     * @memberof BonusInfoFormComponent
     */
    submitBonusInfo(bonusInfo: BonusInfo, bonusInfoFormValidated: boolean) {
        this.bonusInfoFormValidated = bonusInfoFormValidated;
        this.bonusInfoSubmitOnInputChange.emit(this.bonusInfoFormValidated);
        if (bonusInfo) {
            this.bonusInformation = this._commonService.trimObjectValues(bonusInfo);
        } else {
            this.hideErrorElement(false);
        }
        
        if(!sessionStorage.getItem('customError-accountNumber'))
            this.bonusInfoSubmit.emit(this.bonusInformation);
            
        
    }

    /**
     * @description this metho will used to translate the data
     * for schema form fields
     * @date 2020-09-15
     * @param {*} jsonObject
     * @memberof BonusInfoFormComponent
     */
    getTranslatedSchemaData(jsonObject: any): void {
        // Translate Schema form fields
        if (jsonObject && jsonObject.schema && jsonObject.schema.properties) {
            const jsonSchemaKeys = Object.keys(jsonObject.schema.properties);
            if (jsonSchemaKeys) {
                jsonSchemaKeys.forEach((obj: any) => {
                    // Translate Schema Form fields..
                    let translatedText = jsonObject.schema.properties[obj].placeholder || jsonObject.schema.properties[obj].selectText;
                    if (translatedText.endsWith('*')) {
                        translatedText = translatedText.slice(0, -1);
                    }
                    let translatedValue = this._translatePipe.get('schemaform.' + translatedText)['value'];
                    translatedValue = translatedValue ? translatedValue : translatedText;
                    if (jsonObject.schema.required) {
                        if (jsonObject.schema.required.indexOf(obj) > -1) {
                            translatedValue += '*';
                        }
                    }
                    const translateKeyValue = this._translateKey.transform(translatedValue, false);
                    translatedValue = translateKeyValue ? translateKeyValue : translatedValue;
                    if (jsonObject.schema.properties[obj].placeholder) {
                        jsonObject.schema.properties[obj].placeholder = translatedValue;
                    } else if (jsonObject.schema.properties[obj].selectText) {
                        jsonObject.schema.properties[obj].selectText = translatedValue;
                    }
                    // Unique Validation Message Translation
                    if (jsonObject.schema.properties[obj].uniqueValidationMessage) {
                        const errorTranslatedText = jsonObject.schema.properties[obj].uniqueValidationMessage;
                        let errorTranslatedValue = this._translatePipe.get('schemaform.' + errorTranslatedText)['value'];
                        errorTranslatedValue = errorTranslatedValue ? errorTranslatedValue : errorTranslatedText;
                        const errorTranslateKeyValue = this._translateKey.transform(errorTranslatedValue, false);
                        errorTranslatedValue = errorTranslateKeyValue ? errorTranslateKeyValue : errorTranslatedValue;
                        jsonObject.schema.properties[obj].uniqueValidationMessage = errorTranslatedValue;
                    }
                    // Translate Validation messages..
                    if (jsonObject.schema.properties[obj].validationMessages) {
                        const validationMessagesKeys = Object.keys(jsonObject.schema.properties[obj].validationMessages);
                        validationMessagesKeys.forEach((validationObj) => {
                            if (validationObj) {
                                const errorTranslatedText = jsonObject.schema.properties[obj].validationMessages[validationObj];
                                if (errorTranslatedText) {
                                    let errorTranslatedValue = this._translatePipe.get('schemaform.' + errorTranslatedText)['value'];
                                    errorTranslatedValue = errorTranslatedValue ? errorTranslatedValue : errorTranslatedText;
                                    const errorTranslateKeyValue = this._translateKey.transform(errorTranslatedValue, false);
                                    errorTranslatedValue = errorTranslateKeyValue ? errorTranslateKeyValue : errorTranslatedValue;
                                    jsonObject.schema.properties[obj].validationMessages[validationObj] = errorTranslatedValue;
                                }
                            }
                        });
                    }
                });
            }
        }
        // Translate Schema form Button Text..
        if (jsonObject && (jsonObject.form || jsonObject.layout)) {
            const jsonObjectForm = jsonObject.form || jsonObject.layout;
            for (let i = 0; i < jsonObjectForm.length; i++) {
                jsonObjectForm[i].items = this.searchButtonTitle(jsonObjectForm[i].items);
            }
        }
        this.displayBonusInfoForm = true;
    }
    /** Search Button text
     * @param  {any} items
     */
    searchButtonTitle(items: any) {
        if (items) {
            items.forEach(element => {
                if (element.title) {
                    const translatedText = element.title;
                    let translatedValue = this._translatePipe.get('schemaform.' + translatedText)['value'];
                    translatedValue = this._translateKey.transform(translatedValue, false);
                    element.title = translatedValue;
                }
                if (element.items && element.items instanceof Array) {
                    const nestedItems = element.items;
                    this.searchButtonTitle(nestedItems);
                }
            });
        }
        return items;
    }

    /**
      */
    ngOnDestroy(): void {
        this._chRef.detach();
        if(this.customSelectSubscription){
            this.customSelectSubscription.unsubscribe();
        }
        if(this.bonusInfoFormSubscription){
            this.bonusInfoFormSubscription.unsubscribe();
        }
        if(this.disableBonusInfoFormSubscription){
            this.disableBonusInfoFormSubscription.unsubscribe();
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/customerservice/components/enrollment/bonus-schema-info.component.ts